import React, { useEffect, useMemo, useState } from "react";
import {
    Col, DatePicker, Divider, Input, Row, Select, Switch, Tooltip, Typography, Space,
} from "antd";
import { CloseOutlined, ExperimentOutlined, SaveOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { PermissionedButton as PermissionedButtonBase } from "@components/PermissionedComponents";
const PermissionedButton = PermissionedButtonBase as unknown as React.FC<any>;
import { ColorPickerWithLabel } from "../OverviewSettings/ColorPickerWithLabel";
import { FlagConfig, UserDefinedFlag, UserDefinedFlagType } from "@store/reducers/userDefinedFlags.reducer";
import { trainingAPI } from "@api/TrainingAPI";
import { OptionGroupSelect } from "@components/Compare/Components_1v1/OptionGroupSelect";
import FlagConfigBuilder from "./FlagConfigBuilder";
import BackTestHeatmap from "./BackTestHeatmap";
import { VariableOption, ConditionValidation, BackTestResult } from "./types";
import { extractOperands } from "./utils";
import {
    FLAG_TYPE_COACH, FLAG_TYPE_HINT, FLAG_TYPE_LOCKED_HINT, FLAG_TYPE_LOGIC, FLAG_TYPE_OPTIONS,
} from "./constants";

const { Text } = Typography;
const { TextArea } = Input;

const emptyConfig = (): FlagConfig => ({ condition: "", fetch_params: {} });

export interface FlagFormProps {
    initial: UserDefinedFlag | Omit<UserDefinedFlag, "id">;
    variableOptions: VariableOption[];
    metricTreeData: any[];
    players: any[];
    positionEntities: any[];
    playerGroups: any[];
    isExpanded?: boolean;
    onSave: (data: Omit<UserDefinedFlag, "id">) => void;
    onCancel: () => void;
    saving: boolean;
}

const FlagForm: React.FC<FlagFormProps> = ({
    initial,
    variableOptions,
    metricTreeData,
    players,
    positionEntities,
    playerGroups,
    isExpanded,
    onSave,
    onCancel,
    saving,
}) => {
    // Flags saved before the type column existed come back without one; they all carry a
    // condition, so they are logic flags.
    const initialType: UserDefinedFlagType = initial.type ?? FLAG_TYPE_LOGIC;
    // A coach flag has no rule, so it stores a blank config rather than a condition.
    const configFor = (type: UserDefinedFlagType, existing?: FlagConfig): FlagConfig =>
        type === FLAG_TYPE_COACH ? {} : existing ?? emptyConfig();
    // The backend writes a different Flags row per type, so it refuses to change the type
    // of a flag that already exists. Offer the choice on creation only.
    const isTypeLocked = "id" in initial && initial.id != null;

    const [form, setForm] = useState<Omit<UserDefinedFlag, "id">>({
        name: initial.name,
        abbreviation: initial.abbreviation,
        description: initial.description || "",
        color: initial.color,
        is_active: initial.is_active,
        type: initialType,
        config: configFor(initialType, initial.config),
    });

    const isCoach = form.type === FLAG_TYPE_COACH;

    const [showTestPanel, setShowTestPanel] = useState(false);
    const [testSelection, setTestSelection] = useState<string[]>([]);
    const [testDateRange, setTestDateRange] = useState<[Dayjs, Dayjs] | null>(null);
    const [testing, setTesting] = useState(false);
    const [configuringTest, setConfiguringTest] = useState(false);
    const [testResult, setTestResult] = useState<BackTestResult | null>(null);
    const [conditionValid, setConditionValid] = useState<boolean | null>(null);
    const [validatingCondition, setValidatingCondition] = useState(false);

    // Reset test panel state when the flag card collapses
    useEffect(() => {
        if (isExpanded === false) {
            setShowTestPanel(false);
            setTestResult(null);
            setTestSelection([]);
            setTestDateRange(null);
        }
    }, [isExpanded]);

    // Real-time condition validation with debounce
    useEffect(() => {
        const condition = form.config?.condition?.trim();
        if (!condition) {
            setConditionValid(null);
            return;
        }

        const timer = setTimeout(async () => {
            setValidatingCondition(true);
            try {
                const result: ConditionValidation = await trainingAPI.validateUserDefinedFlagCondition(condition);
                setConditionValid(result.valid);
            } catch {
                setConditionValid(false);
            } finally {
                setValidatingCondition(false);
            }
        }, 800);

        return () => clearTimeout(timer);
    }, [form.config?.condition]);

    // Unified player / position / group options — same structure as compare range page
    const playerPosGroupOptions = useMemo(() => {
        const opts: any[] = [
            {
                label: <span>Team</span>,
                title: "Team",
                options: [{ label: <span>Full Team</span>, value: "full-team", key: "full-team" }],
            },
        ];

        if (players.length > 0) {
            opts.push({
                label: <span>Players</span>,
                title: "Players",
                options: [...players]
                    .sort((a, b) =>
                        `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
                    )
                    .map((p) => ({
                        label: <span>{`${p.first_name} ${p.last_name}`}</span>,
                        value: `player-${p.player_id}`,
                        key: p.player_id,
                    })),
            });
        }

        if (positionEntities.length > 0) {
            opts.push({
                label: <span>Positions</span>,
                title: "Positions",
                options: [...positionEntities]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((p) => ({
                        label: <span>{p.name}</span>,
                        value: `pos-${p.name}`,
                        key: p.name,
                    })),
            });
        }

        if (playerGroups.length > 0) {
            opts.push({
                label: <span>Groups</span>,
                title: "Groups",
                options: [...playerGroups]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((g) => ({
                        label: <span>{g.name}</span>,
                        value: `group-${g.player_group_id}`,
                        key: g.player_group_id,
                    })),
            });
        }

        return opts;
    }, [players, positionEntities, playerGroups]);

    const isDirty = useMemo(() => {
        const initialConfig = configFor(initialType, initial.config);
        return (
            form.name !== initial.name ||
            form.abbreviation !== initial.abbreviation ||
            form.description !== (initial.description || "") ||
            form.color !== initial.color ||
            form.is_active !== initial.is_active ||
            form.type !== initialType ||
            JSON.stringify(form.config) !== JSON.stringify(initialConfig)
        );
    }, [form, initial, initialType]);

    const isValid = useMemo(() => {
        const abbr = form.abbreviation.trim();
        const basicValid = form.name.trim().length > 0 && abbr.length > 0 && abbr.length <= 3 && form.color.trim().length > 0;

        // A coach flag carries no rule, so the identity fields are all there is to check.
        if (isCoach) {
            return basicValid;
        }

        const condition = form.config?.condition?.trim();

        if (!condition) {
            return basicValid;
        }

        if (conditionValid === false) {
            return false;
        }

        return basicValid && conditionValid === true;
    }, [form.name, form.abbreviation, form.color, form.config?.condition, conditionValid, isCoach]);

    const isTestReady = testSelection.length > 0 && testDateRange !== null;

    const handleConfigureTest = async () => {
        if (showTestPanel) {
            setShowTestPanel(false);
            return;
        }
        const condition = form.config?.condition?.trim();
        if (!condition) {
            message.error("Please enter a rule first");
            return;
        }
        const ops = extractOperands(condition);
        if (ops.length === 0) {
            message.error("Add at least one operand to the rule");
            return;
        }
        const incomplete = ops
            .filter((op) => {
                const p = form.config?.fetch_params?.[op];
                return !p?.metric_identifier || !p?.scope || !p?.variable;
            })
            .map((op) => form.config?.fetch_params?.[op]?.label || op);
        if (incomplete.length > 0) {
            message.error(`Finish setting up all fields for: ${incomplete.join(", ")}`);
            return;
        }
        setConfiguringTest(true);
        try {
            const result: ConditionValidation = await trainingAPI.validateUserDefinedFlagCondition(condition);
            if (!result.valid) {
                message.error("Rule is invalid. Please fix it before running a back test.");
                return;
            }
            setTestSelection([]);
            setTestDateRange(null);
            setTestResult(null);
            setShowTestPanel(true);
        } catch {
            message.error("Failed to validate rule");
        } finally {
            setConfiguringTest(false);
        }
    };

    const handleRunBackTest = async () => {
        if (!isTestReady) return;
        setTesting(true);
        try {
            const isFullTeam = testSelection.includes("full-team");
            const player_ids = isFullTeam
                ? players.filter((p) => !p.retired).map((p) => p.player_id)
                : testSelection
                      .filter((v) => v.startsWith("player-"))
                      .map((v) => parseInt(v.slice(7), 10));
            const positions = isFullTeam
                ? []
                : testSelection
                      .filter((v) => v.startsWith("pos-"))
                      .map((v) => v.slice(4));
            const player_groups = isFullTeam
                ? []
                : testSelection
                      .filter((v) => v.startsWith("group-"))
                      .map((v) => parseInt(v.slice(6), 10));

            const data = await trainingAPI.backTestUserDefinedFlag(
                form.config,
                testDateRange![0].format("YYYY-MM-DD"),
                testDateRange![1].format("YYYY-MM-DD"),
                { player_ids, positions, player_groups },
            );
            setTestResult(data);
        } catch {
            message.error("Back test failed");
        } finally {
            setTesting(false);
        }
    };

    const resetTestState = () => {
        setShowTestPanel(false);
        setTestResult(null);
        setTestSelection([]);
        setTestDateRange(null);
    };

    // Switching to coach drops the rule entirely: the condition box is cleared and the
    // backend is sent a blank config. Switching back starts from a blank rule.
    const handleTypeChange = (type: UserDefinedFlagType) => {
        setForm((f) => ({ ...f, type, config: configFor(type) }));
        setConditionValid(null);
        resetTestState();
    };

    return (
        <div className="udf-form">
            <Row gutter={[16, 12]}>
                <Col span={9}>
                    <div className="udf-form-field">
                        <Text strong>Name</Text>
                        <Input
                            value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                            placeholder="Flag name"
                            maxLength={150}
                        />
                    </div>
                </Col>
                <Col span={5}>
                    <div className="udf-form-field">
                        <Text strong>
                            Abbreviation{" "}
                            <Tooltip title="Max 3 characters">
                                <Text type="secondary" style={{ fontSize: 11 }}>(max 3)</Text>
                            </Tooltip>
                        </Text>
                        <Input
                            value={form.abbreviation}
                            onChange={(e) => {
                                if (e.target.value.length <= 3)
                                    setForm((f) => ({ ...f, abbreviation: e.target.value }));
                            }}
                            placeholder="e.g. HRV"
                            maxLength={3}
                            status={form.abbreviation.length > 3 ? "error" : ""}
                        />
                    </div>
                </Col>
                <Col span={5}>
                    <div className="udf-form-field">
                        <Text strong>Type</Text>
                        <Tooltip title={isTypeLocked ? FLAG_TYPE_LOCKED_HINT : FLAG_TYPE_HINT}>
                            <Select
                                style={{ width: "100%" }}
                                value={form.type}
                                options={FLAG_TYPE_OPTIONS}
                                disabled={isTypeLocked}
                                onChange={handleTypeChange}
                            />
                        </Tooltip>
                    </div>
                </Col>
                <Col span={5}>
                    <div className="udf-form-field">
                        <Text strong>Active</Text>
                        <div style={{ paddingTop: 6 }}>
                            <Switch
                                checked={form.is_active}
                                onChange={(checked) => setForm((f) => ({ ...f, is_active: checked }))}
                            />
                        </div>
                    </div>
                </Col>
                <Col span={18}>
                    <div className="udf-form-field">
                        <Text strong>Description</Text>
                        <TextArea
                            value={form.description}
                            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                            placeholder="Describe this flag..."
                            rows={2}
                            maxLength={5000}
                        />
                    </div>
                </Col>
                <Col span={6}>
                    <div className="udf-form-field">
                        <Text strong>Color</Text>
                        <div style={{ paddingTop: 4 }}>
                            <ColorPickerWithLabel
                                pickerId="udf-color"
                                defaultColor={form.color}
                                handleColorChangeParent={(_: string, hex: string) =>
                                    setForm((f) => ({ ...f, color: hex }))
                                }
                            />
                        </div>
                    </div>
                </Col>
            </Row>

            {!isCoach && (
                <>
                    <Divider style={{ margin: "12px 0" }} />

                    <FlagConfigBuilder
                        config={form.config}
                        onChange={(config) => setForm((f) => ({ ...f, config }))}
                        variableOptions={variableOptions}
                        metricTreeData={metricTreeData}
                    />

                    {form.config?.condition?.trim() && conditionValid === false && (
                        <div style={{ marginTop: "12px", marginBottom: "12px" }}>
                            <Space style={{ color: "#ff4d4f", fontSize: "13px" }}>
                                <ExclamationCircleOutlined />
                                <span>Rule is invalid</span>
                            </Space>
                        </div>
                    )}
                </>
            )}

            {isCoach && (
                <>
                    <Divider style={{ margin: "12px 0" }} />
                    <Text type="secondary" className="udf-section-hint">
                        Coach flags have no rule to build. Raise this flag on a player yourself
                        whenever you need it.
                    </Text>
                </>
            )}

            <div className="udf-form-actions">
                <PermissionedButton
                    permissionCheck="ManageCustomFlagConfigurations"
                    permissionMessage="You don't have permission to manage user defined flags"
                    onClick={onCancel}
                    icon={<CloseOutlined />}
                >
                    Cancel
                </PermissionedButton>
                <PermissionedButton
                    permissionCheck="ManageCustomFlagConfigurations"
                    permissionMessage="You don't have permission to manage user defined flags"
                    type="primary"
                    onClick={() => onSave(form)}
                    icon={<SaveOutlined />}
                    loading={saving}
                    disabled={!isValid}
                    className={isDirty && isValid ? "pulse" : ""}
                >
                    Save
                </PermissionedButton>
                {!isCoach && (
                    <PermissionedButton
                        permissionCheck="ManageCustomFlagConfigurations"
                        permissionMessage="You don't have permission to manage user defined flags"
                        icon={<ExperimentOutlined />}
                        loading={configuringTest}
                        onClick={handleConfigureTest}
                    >
                        Test your flag
                    </PermissionedButton>
                )}
            </div>

            {showTestPanel && (
                <div className="udf-test-panel">
                    <div className="udf-test-panel-header">
                        <div className="udf-test-panel-heading">
                            <Text strong className="udf-test-panel-title">
                                Back test your flag rule against real data
                            </Text>
                            <Text type="secondary" className="udf-test-panel-subtitle">
                                Use this feature to run experiments to fine-tune your flag rule for your team.
                                <br />
                                Select a date range and a set of players, and we'll tell you which of your players
                                would've been flagged with this rule enabled.
                                <br />
                                The bigger the selected date range, the longer it may take to run the experiment.
                                We recommend no more than 6 months at a time.
                            </Text>
                        </div>
                        <PermissionedButton
                            permissionCheck="ManageCustomFlagConfigurations"
                            permissionMessage="You don't have permission to manage user defined flags"
                            type="text"
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={resetTestState}
                            className="udf-test-panel-close"
                        />
                    </div>
                    <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
                        <Col span={12}>
                            <div className="udf-form-field">
                                <Text strong>Players / Positions / Groups</Text>
                                <OptionGroupSelect
                                    placeholder="Full Team"
                                    options={playerPosGroupOptions}
                                    selectedOptionVal={testSelection}
                                    sideId="back-test"
                                    handleOptionGroupChange={(v: string[]) => {
                                        const hadFullTeam = testSelection.includes("full-team");
                                        const hasFullTeam = v.includes("full-team");
                                        let next = v;
                                        if (hasFullTeam && !hadFullTeam) {
                                            // user just added full-team → supersedes everything else
                                            next = ["full-team"];
                                        } else if (hasFullTeam && hadFullTeam) {
                                            // user added a specific item while full-team was active → drop full-team
                                            next = v.filter((x) => x !== "full-team");
                                        }
                                        setTestSelection(next);
                                        setTestResult(null);
                                    }}
                                />
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="udf-form-field">
                                <Text strong>Date Range</Text>
                                <DatePicker.RangePicker
                                    style={{ width: "100%" }}
                                    value={testDateRange}
                                    onChange={(dates) => {
                                        setTestDateRange(dates as [Dayjs, Dayjs] | null);
                                        setTestResult(null);
                                    }}
                                />
                            </div>
                        </Col>
                    </Row>
                    <div className="udf-test-panel-actions">
                        <PermissionedButton
                            permissionCheck="ManageCustomFlagConfigurations"
                            permissionMessage="You don't have permission to manage user defined flags"
                            type="primary"
                            icon={<ExperimentOutlined />}
                            disabled={!isTestReady}
                            loading={testing}
                            onClick={handleRunBackTest}
                        >
                            Run Experiment
                        </PermissionedButton>
                    </div>
                    {testResult && (
                        <BackTestHeatmap
                            result={testResult}
                            players={players}
                            fetchParams={form.config?.fetch_params}
                            flagColor={form.color}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default FlagForm;
